import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import { ChipList } from '@progress/kendo-react-buttons';
import cn from 'clsx';
import { PanelPreview } from '../panel-preview/panel-preview';
import { BenchmarkType, CountryType, IFormData, OptionsProportionsOT, RowDataDate, RowDataFlag } from './interfaces';
import styles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Card from '@components-clean/core/card';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { FieldDefinitionFieldTypeDto } from '@interfaces-clean/field-rdl-definition.interface';
import { usePanelDetailsContext } from '@pages-clean/admin/study/manage-panels/context';
import { COMPONENT_NAME, ICreateData } from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/shared';
import PanelReasonForChangeDialog from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/panel-reason-for-change-dialog';
import {
    AutomationRange,
    ModelPlotBy,
    ModelPlotCalculation,
    ModelScaleBy,
    PanelInput,
    PanelType,
    ProportionsOTBenchmarkType,
    ProportionsOTCountryBenchmarkType,
    ProportionsOTThresholdType,
    RowDataSourceTag,
} from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';

import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_PANEL, GET_PANELS, SAVE_PANEL } from '@queries/central-monitoring';
import {
    getPanels,
    panel_OpraQueries_panel_Panel,
    panel as IPanel,
    panelVariables as IPanelVariables,
    upsertPanel,
    upsertPanelVariables,
} from '@queries/generated/central-monitoring.generated';
import { useGetLatestDataSet } from '@apis/bff-api/central-monitoring';

interface IProportionsOTProps {
    panel?: panel_OpraQueries_panel_Panel;
    onCancelOverride?: () => void;
    onUpdate: () => void;
    onCreate: (panelRef: string) => void;
    canEdit?: boolean;
    processedConfig?: panel_OpraQueries_panel_Panel;
}

interface IProps {
    panelRef?: string;
    createData?: ICreateData;
    canEdit?: boolean;
}

enum ChipValues {
    Configuration = 1,
    Preview = 2,
}
const ProportionsOT = ({
    panel,
    onCancelOverride,
    onUpdate,
    onCreate,
    canEdit,
    processedConfig,
}: IProportionsOTProps) => {
    const { t } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.proportionsOT' });
    const { t: tOptions } = useTranslation('admin', { keyPrefix: 'managePanels.panelDetails.proportionsOT.options' });
    const { t: tCommon } = useTranslation('common');
    const { t: tManagePanels } = useTranslation('admin', { keyPrefix: 'managePanels' });
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const [chip, setChip] = useState<number>(ChipValues.Configuration);
    const { source } = usePanelDetailsContext();
    const [showReasonForChangeDialog, setShowReasonForChangeDialog] = useState(false);

    const {
        ScaleByOptions,
        EligibilityPeriodOptions,
        PlotByOptions,
        ThresholdTypeOptions,
        BenchmarkTypeOptions,
        CountryBenchmarkTypeOptions,
        PlotCalculationOptions,
        YesNoOptions,
        RangeOptions,
    } = OptionsProportionsOT(tOptions);

    const [savePanel, { loading: isSaving }] = useMutation<upsertPanel, upsertPanelVariables>(SAVE_PANEL);
    const { state } = useLocalState();

    const { data, loading: isLoading } = useQuery<getPanels>(GET_PANELS, {
        variables: {
            organisationId: state?.selectedOrganisation?.id,
            studyId: state?.selectedStudy?.id,
        },
        fetchPolicy: 'no-cache',
    });

    const studyPanels = useMemo(() => {
        return data?.panels?.flatMap((studyPanel) => {
            if (!!studyPanel?.plotTitle && studyPanel.id !== panel?.id) return [studyPanel?.plotTitle];
            return [];
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    const rowDataOptions = state?.cm2DataSources
        ?.flatMap((d) => {
            if (d?.id == null || d?.name == null || d.table == null) return [];

            if (!d?.tags?.includes(RowDataSourceTag.PANEL) || d?.default) return [];

            return [
                {
                    id: d?.id,
                    name: d?.name,
                    table: d.table,
                },
            ];
        })
        .sort((a, b) => a.name.localeCompare(b.name));

    const getFieldOptions = (table: string, fieldTypes: FieldDefinitionFieldTypeDto[]) => {
        return state?.cm2Fields
            ?.filter((f) => {
                if (f?.fieldType == null) return false;
                if (table !== f?.dataType) return false;
                if (f.isHidden) return false;

                return fieldTypes.includes(f?.fieldType);
            })
            .flatMap((f) => {
                if (f?.key == null || f.name == null) return [];

                return [
                    {
                        id: f?.key,
                        name: f.name,
                    },
                ];
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    };
    const getRowDataFlagOptions = (table: string) =>
        getFieldOptions(table, [
            FieldDefinitionFieldTypeDto.DATE,
            FieldDefinitionFieldTypeDto.PARTIAL_DATE,
            FieldDefinitionFieldTypeDto.BOOL,
        ]);

    const getRowDataDateOptions = (table: string) =>
        getFieldOptions(table, [FieldDefinitionFieldTypeDto.DATE, FieldDefinitionFieldTypeDto.PARTIAL_DATE]);

    const [rowDataDateOptions, setRowDataDateOptions] = useState<RowDataDate[] | undefined>(() =>
        getRowDataDateOptions(
            rowDataOptions?.find((r) => r.id === panel?.proportionsOverTimeParameters?.rowData)?.table || '',
        ),
    );

    const [rowDataFlagOptions, setRowDataFlagOptions] = useState<RowDataFlag[] | undefined>(() =>
        getRowDataFlagOptions(
            rowDataOptions?.find((r) => r.id === panel?.proportionsOverTimeParameters?.rowData)?.table || '',
        ),
    );

    const [updatedBenchMarkOptions, setUpdatedBenchMarkOptions] = useState<BenchmarkType[] | undefined>(
        BenchmarkTypeOptions,
    );

    const [updatedCountryTypeOptions, setUpdatedCountryTypeOptions] = useState<CountryType[] | undefined>(
        CountryBenchmarkTypeOptions,
    );

    const defaultFormValues = useMemo<IFormData>(() => {
        const rowData =
            (panel?.proportionsOverTimeParameters?.rowData &&
                rowDataOptions?.find((r) => r.id === panel?.proportionsOverTimeParameters?.rowData)) ||
            null;

        return {
            plotTitle: panel?.name ?? '',
            rowData,
            qtl:
                (panel?.proportionsOverTimeParameters?.qtl && YesNoOptions.find((o) => o.id === 'true')) ||
                YesNoOptions.find((o) => o.id === 'false'),
            reportOOT: panel?.proportionsOverTimeParameters?.reportOutOfTolerance
                ? YesNoOptions.find((o) => o.id === 'true')
                : panel?.proportionsOverTimeParameters?.reportOutOfTolerance === false
                  ? YesNoOptions.find((o) => o.id === 'false')
                  : null,
            rowDataDate:
                (rowData &&
                    panel?.proportionsOverTimeParameters?.rowDataDate &&
                    getRowDataDateOptions(rowData.table)?.find(
                        (s) => s.id === panel?.proportionsOverTimeParameters?.rowDataDate,
                    )) ||
                null,
            rowDataFlag:
                (rowData &&
                    panel?.proportionsOverTimeParameters?.rowDataFlag &&
                    getRowDataFlagOptions(rowData.table)?.find(
                        (s) => s.id === panel?.proportionsOverTimeParameters?.rowDataFlag,
                    )) ||
                null,
            scaleBy:
                (panel?.proportionsOverTimeParameters?.scaleBy &&
                    ScaleByOptions.find((o) => o.id === panel?.proportionsOverTimeParameters?.scaleBy)) ||
                ScaleByOptions.find((o) => o.id === ModelScaleBy._1),
            plotBy:
                (panel?.proportionsOverTimeParameters?.plotBy &&
                    PlotByOptions.find((o) => o.id === panel?.proportionsOverTimeParameters?.plotBy)) ||
                PlotByOptions.find((o) => o.id === ModelPlotBy.MONTHS),
            plotCalculations:
                (panel?.proportionsOverTimeParameters?.cumulativeOrTimePeriod &&
                    PlotCalculationOptions.find(
                        (o) => o.id === panel?.proportionsOverTimeParameters?.cumulativeOrTimePeriod,
                    )) ||
                PlotCalculationOptions.find((o) => o.id === ModelPlotCalculation.CUMULATIVE),
            xAxisPtsToShade: panel?.proportionsOverTimeParameters?.xAxisPtsToShade ?? null,
            minData: panel?.proportionsOverTimeParameters?.minData ?? null,
            thresholdType:
                (panel?.proportionsOverTimeParameters?.thresholdType &&
                    ThresholdTypeOptions.find((o) => o.id === panel?.proportionsOverTimeParameters?.thresholdType)) ||
                null,
            upperRed: panel?.proportionsOverTimeParameters?.upperRed ?? null,
            upperAmber: panel?.proportionsOverTimeParameters?.upperAmber ?? null,
            lowerRed: panel?.proportionsOverTimeParameters?.lowerRed ?? null,
            lowerAmber: panel?.proportionsOverTimeParameters?.lowerAmber ?? null,
            eligibilityUnit: panel?.proportionsOverTimeParameters?.eligibilityUnit ?? null,
            eligibilityPeriod:
                (panel?.proportionsOverTimeParameters?.eligibilityPeriod &&
                    EligibilityPeriodOptions.find(
                        (o) => o.id === panel?.proportionsOverTimeParameters?.eligibilityPeriod,
                    )) ||
                null,
            studyType:
                (panel?.proportionsOverTimeParameters?.thresholdType &&
                    BenchmarkTypeOptions.find((o) => o.id === panel?.proportionsOverTimeParameters?.benchmarkType)) ||
                null,
            studySpecificValue: panel?.proportionsOverTimeParameters?.studySpecificBenchmark ?? null,
            countryValue:
                (panel?.proportionsOverTimeParameters?.countryBenchmark &&
                    CountryBenchmarkTypeOptions.find(
                        (o) => o.id === panel?.proportionsOverTimeParameters?.countryBenchmark,
                    )) ||
                null,
            numeratorName: panel?.proportionsOverTimeParameters?.numeratorName ?? null,
            denominatorName: panel?.proportionsOverTimeParameters?.denominatorName ?? null,
            expectedVolume: panel?.proportionsOverTimeParameters?.expectedVolume ?? null,

            xAxisTitle: panel?.proportionsOverTimeParameters?.xAxisTitle ?? '',
            yAxisTitle: panel?.proportionsOverTimeParameters?.yAxisTitle ?? '',
            isActive: panel?.active ?? true,
            configId: panel?.configId,
            numberOfTimePeriods: panel?.proportionsOverTimeParameters?.numberOfTimePeriods ?? null,
            automationRange:
                (panel?.proportionsOverTimeParameters?.automationRange &&
                    RangeOptions.find((o) => o.id === panel?.proportionsOverTimeParameters?.automationRange)) ||
                null,
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [panel]);

    const {
        control,
        reset,
        setValue,
        handleSubmit,
        getValues,
        watch,
        trigger,
        formState: { isDirty, isValid },
    } = useForm<IFormData>({
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });

    const [isEditing, setIsEditing] = useState(!panel);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const disableThresholds = useMemo(() => {
        return getValues('thresholdType') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('thresholdType')]);

    const disableRowDataFields = useMemo(() => {
        return getValues('rowData') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('rowData')]);

    const disableCountryType = useMemo(() => {
        return getValues('studyType') == null;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('studyType')]);

    const showStudySpecific = useMemo(() => {
        const benchMarkType = getValues('studyType');
        return benchMarkType?.id === ProportionsOTBenchmarkType.STUDY_SPECIFIC;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('studyType')]);

    const disableAutomationRange = useMemo(() => {
        const plotCalculations = getValues('plotCalculations');
        return plotCalculations?.id !== ModelPlotCalculation.BY_TIME_PERIOD;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('plotCalculations')]);

    const enableNoOfTimePeriods = useMemo(() => {
        const range = getValues('automationRange');
        const plotCalculations = getValues('plotCalculations');
        return (
            range?.id === AutomationRange.SPECIFIC_RANGE && plotCalculations?.id === ModelPlotCalculation.BY_TIME_PERIOD
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [watch('automationRange')]);

    const getScaleByNumber = (scaleBy: ModelScaleBy | undefined) => {
        let scaleByValue = 1;
        switch (scaleBy) {
            case ModelScaleBy._1:
                scaleByValue = 1;
                break;
            case ModelScaleBy._10:
                scaleByValue = 10;
                break;
            case ModelScaleBy._100:
                scaleByValue = 100;
                break;
            case ModelScaleBy._1000:
                scaleByValue = 1000;
                break;
            default:
                return undefined;
        }
        return scaleByValue;
    };

    const submit = async (reasonForChange?: string) => {
        await handleSubmit(async (formData) => {
            try {
                if (!isValid) return;

                const savingPanel: PanelInput = {
                    id: panel?.id,
                    configId: panel?.configId,

                    organisationId: state?.organisation?.id,
                    studyId: state?.selectedStudy?.id,
                    panelType: PanelType.PROPORTIONS_OVER_TIME,

                    name: formData.plotTitle,
                    // TODO: Once we work out how to move to the Name field exclusively, remove this duplication
                    graph: {
                        plotTitle: formData.plotTitle,
                    },

                    active: formData.isActive,

                    proportionsOverTimeParameters: {
                        qtl: formData.qtl?.id === 'true',
                        xAxisTitle: formData.xAxisTitle,
                        yAxisTitle: formData.yAxisTitle,
                        rowData: formData.rowData?.id,
                        rowDataFlag: formData.rowDataFlag?.id,
                        rowDataDate: formData.rowDataDate?.id,
                        scaleBy: formData.scaleBy?.id,
                        plotBy: formData.plotBy?.id,
                        cumulativeOrTimePeriod: formData.plotCalculations?.id,
                        xAxisPtsToShade: formData.xAxisPtsToShade,
                        minData: formData.minData,
                        thresholdType: formData.thresholdType?.id,
                        upperRed: formData.upperRed,
                        upperAmber: formData.upperAmber,
                        lowerRed: formData.lowerRed,
                        lowerAmber: formData.lowerAmber,
                        eligibilityUnit: formData.eligibilityUnit,
                        eligibilityPeriod: formData.eligibilityPeriod?.id,
                        benchmarkType: formData.studyType?.id,
                        studySpecificBenchmark: formData.studySpecificValue,
                        countryBenchmark: formData.countryValue?.id,
                        numeratorName: formData.numeratorName,
                        denominatorName: formData.denominatorName,
                        expectedVolume: formData.expectedVolume,
                        reportOutOfTolerance: formData.reportOOT?.id === 'true',
                        numberOfTimePeriods: formData.numberOfTimePeriods,
                        automationRange: formData.automationRange?.id,
                    },
                    associatedRisks: panel?.associatedRisksIds,
                };

                const result = await savePanel({
                    variables: {
                        panel: savingPanel,
                        reason: reasonForChange ?? '',
                    },
                });

                if (result.data?.upsertPanel == null) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }

                ShowSuccess(tCommon('actionsSuccessfulToast'));
                setIsEditing(false);

                if (panel) {
                    onUpdate();
                } else {
                    await onCreate(result.data?.upsertPanel?.id!);
                }
            } catch (error) {
                ShowError(tCommon('errorMessage'));
                throw error;
            }
        })();
    };

    const updateChip = (tabIndex: number) => {
        if (tabIndex !== null) {
            setChip(tabIndex);
        }
    };

    // If any new field is added in the panel config that affects the panel preview, then it needs to be added here too
    // as then we need it for preview.

    const currentPanel = getValues();
    const currentConfig = {
        ...panel,
        plotTitle: currentPanel?.plotTitle,
        panelType: PanelType.PROPORTIONS_OVER_TIME,
        proportionsOverTimeParameters: {
            benchmarkType: currentPanel.studyType?.id,
            countryBenchmark: currentPanel.countryValue?.id,
            cumulativeOrTimePeriod: currentPanel.plotCalculations?.id,
            denominatorName: currentPanel.denominatorName,
            eligibilityPeriod: currentPanel.eligibilityPeriod?.id,
            eligibilityUnit: currentPanel.eligibilityUnit,
            expectedVolume: currentPanel.expectedVolume,
            lowerAmber: currentPanel.lowerAmber,
            lowerRed: currentPanel.lowerRed,
            minData: currentPanel.minData,
            numeratorName: currentPanel.numeratorName,
            plotBy: currentPanel.plotBy?.id,
            qtl: currentPanel.qtl?.id,
            reportOutOfTolerance: currentPanel.reportOOT?.id,
            rowData: currentPanel.rowData?.id,
            rowDataDate: currentPanel.rowDataDate?.id,
            rowDataFlag: currentPanel.rowDataFlag?.id,
            scaleBy: currentPanel.scaleBy?.id,
            studySpecificBenchmark: currentPanel.studySpecificValue,
            thresholdType: currentPanel.thresholdType?.id,
            upperRed: currentPanel.upperRed,
            upperAmber: currentPanel.upperAmber,
            xAxisPtsToShade: currentPanel.xAxisPtsToShade,
            xAxisTitle: currentPanel.xAxisTitle,
            yAxisTitle: currentPanel.yAxisTitle,
            numberOfTimePeriods: currentPanel.numberOfTimePeriods,
            automationRange: currentPanel.automationRange?.id,
            __typename: 'ProportionsOverTime',
        },
    };

    const content = (
        <div className="k-h-full">
            <div className="k-pt-2 k-pl-2 k-pr-2 k-mb-3">
                <Card data-testid="prop-ot-panel-preview" className="k-min-h-min" bodyClassName="!k-p-1">
                    <ChipList
                        data-testid="panel-preview"
                        defaultData={[
                            { text: tManagePanels('configuration'), value: ChipValues.Configuration },
                            { text: tManagePanels('preview'), value: ChipValues.Preview },
                        ]}
                        value={chip}
                        onChange={(event) => {
                            updateChip(event.value as number);
                        }}
                        selection="single"
                    />
                </Card>
            </div>
            {chip === ChipValues.Configuration && (
                <div className={cn(styles.proportionsOTContainer)}>
                    <div className={cn(styles.proportionsOT)}>
                        {/* COLUMN 1 */}
                        <div
                            className={cn(
                                styles.borderRight,
                                'k-d-flex-col k-pr-3 k-pl-3 k-py-3 k-col-span-1 k-overflow-y-auto',
                            )}
                        >
                            <div className="k-d-flex-row k-align-items-center">
                                <div className="k-mr-4 k-min-w-fit">
                                    <CheckboxController
                                        data-testid="is-active"
                                        label={t('labels.active')}
                                        name="isActive"
                                        control={control}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className={cn(styles.borderLeft, 'k-pl-4 k-flex-grow')}>
                                    <TextBoxController
                                        label={t('labels.configId')}
                                        data-testid="config-id"
                                        name="configId"
                                        control={control}
                                        readOnly
                                        copyBtn
                                    />
                                </div>
                            </div>
                            <div>
                                <TextBoxController
                                    label={t('labels.plotTitle')}
                                    data-testid="plot-title"
                                    name="plotTitle"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                    validateRules={{
                                        uniqueTitle: (value) => {
                                            const plotTitle = value as string;
                                            if (plotTitle != null && studyPanels?.includes(plotTitle))
                                                return t('validation.uniquePlotTitle');
                                        },
                                    }}
                                />
                            </div>
                            <div className="k-mt--2">
                                <TextBoxController
                                    label={t('labels.xAxisTitle')}
                                    data-testid="x-axis-title"
                                    name="xAxisTitle"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="k-mt--2">
                                <TextBoxController
                                    label={t('labels.yAxisTitle')}
                                    data-testid="y-axis-title"
                                    name="yAxisTitle"
                                    control={control}
                                    required
                                    readOnly={!isEditing}
                                    maxLength={50}
                                />
                            </div>

                            <div className="k-d-flex-row k-gap-3 k-justify-content-evenly k-w-full">
                                <BasicDropDownController
                                    label={t('labels.qtl')}
                                    control={control}
                                    name="qtl"
                                    data-testid="qtl"
                                    data={YesNoOptions}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />

                                <BasicDropDownController
                                    label={t('labels.reportoot')}
                                    control={control}
                                    name="reportOOT"
                                    data-testid="report-oot"
                                    data={YesNoOptions}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                        </div>
                        {/* COLUMN 2 */}
                        <div
                            className={cn(
                                styles.borderRight,
                                'k-d-flex-col k-gap-3 k-justify-content-start k-overflow-y-auto k-px-4 k-pb-3',
                            )}
                        >
                            <div className="k-d-flex-row k-mt-3">
                                <BasicDropDownController
                                    label={t('labels.rowData')}
                                    control={control}
                                    name="rowData"
                                    data-testid="row-data"
                                    data={rowDataOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                    onChange={async (event) => {
                                        const { value } = event;
                                        if (value) {
                                            const rowDate = getRowDataDateOptions(value.table);
                                            setRowDataDateOptions(rowDate);
                                            setValue('rowDataDate', null);
                                            const rowFlag = getRowDataFlagOptions(value.table);
                                            setRowDataFlagOptions(rowFlag);
                                            setValue('rowDataFlag', null);
                                        }
                                    }}
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <BasicDropDownController
                                    label={t('labels.rowDataDate')}
                                    control={control}
                                    name="rowDataDate"
                                    data-testid="row-data-date"
                                    data={rowDataDateOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                    disabled={disableRowDataFields}
                                />
                            </div>
                            <div className="k-d-flex-row">
                                <BasicDropDownController
                                    label={t('labels.rowDataFlag')}
                                    control={control}
                                    name="rowDataFlag"
                                    data-testid="row-data-flag"
                                    data={rowDataFlagOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                    disabled={disableRowDataFields}
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3">
                                <BasicDropDownController
                                    label={t('labels.scaleBy')}
                                    control={control}
                                    name="scaleBy"
                                    data-testid="scale-by"
                                    data={ScaleByOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                    onChange={async () => {
                                        const studyType = getValues('studyType')?.id;
                                        if (studyType === ProportionsOTBenchmarkType.STUDY_SPECIFIC)
                                            await trigger('studySpecificValue');

                                        const thresholdType = getValues('thresholdType')?.id;
                                        if (thresholdType === ProportionsOTThresholdType.FIXED) {
                                            await trigger('upperRed');
                                            await trigger('upperAmber');
                                            await trigger('lowerAmber');
                                            await trigger('lowerRed');
                                        }
                                    }}
                                />
                                <BasicDropDownController
                                    label={t('labels.plotBy')}
                                    control={control}
                                    name="plotBy"
                                    data-testid="plot-by"
                                    data={PlotByOptions || []}
                                    dataItemKey="id"
                                    textField="name"
                                    required
                                    allowClear
                                    readOnly={!isEditing}
                                    filterable
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3">
                                <div className="k-flex-1">
                                    <BasicDropDownController
                                        label={t('labels.plotCalculations')}
                                        control={control}
                                        name="plotCalculations"
                                        data-testid="plot-calculation"
                                        data={PlotCalculationOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        onChange={async (event) => {
                                            const { value } = event;
                                            if (value && value.id === ModelPlotCalculation.BY_TIME_PERIOD) {
                                                setValue(
                                                    'automationRange',
                                                    RangeOptions.find((i) => i.id === AutomationRange.SPECIFIC_RANGE),
                                                );
                                                setValue('numberOfTimePeriods', 6);
                                            } else {
                                                setValue('automationRange', null);
                                                setValue('numberOfTimePeriods', null);
                                            }

                                            await trigger(['automationRange']);
                                            await trigger(['numberOfTimePeriods']);
                                        }}
                                    />
                                </div>
                                <div className="k-flex-1">
                                    <NumericTextBoxController
                                        label={t('labels.minData')}
                                        control={control}
                                        name="minData"
                                        data-testid="min-data"
                                        readOnly={!isEditing}
                                        min={1}
                                        validationMessage={t('validation.valueGreaterThan0')}
                                    />
                                </div>
                            </div>
                            <Card
                                data-testid="tri-event-rates-ot-automation"
                                className="k-min-h-min"
                                title={t('automation')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-row k-gap-3">
                                    <div className="k-flex-1">
                                        <BasicDropDownController
                                            label={t('labels.range')}
                                            control={control}
                                            name="automationRange"
                                            data-testid="automation-Range"
                                            data={RangeOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                            disabled={disableAutomationRange}
                                            defaultValue={RangeOptions.find(
                                                (i) => i.id === AutomationRange.SPECIFIC_RANGE,
                                            )}
                                            onChange={async (event) => {
                                                const { value } = event;
                                                if (value && value.id === AutomationRange.SPECIFIC_RANGE) {
                                                    setValue('numberOfTimePeriods', 6);
                                                } else {
                                                    setValue('numberOfTimePeriods', null);
                                                }
                                                await trigger(['numberOfTimePeriods']);
                                            }}
                                            validateRules={{
                                                required: (value) => {
                                                    const plotCalculations = getValues('plotCalculations');
                                                    const range = value?.id as string;
                                                    if (
                                                        plotCalculations != null &&
                                                        plotCalculations.id === ModelPlotCalculation.BY_TIME_PERIOD &&
                                                        range == null
                                                    )
                                                        return t('validation.required');
                                                    return true;
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.noOfTimePeriods')}
                                            control={control}
                                            name="numberOfTimePeriods"
                                            data-testid="number-Of-Time-Periods"
                                            readOnly={!isEditing}
                                            min={1}
                                            validationMessage={t('validation.valueGreaterThan0')}
                                            disabled={!enableNoOfTimePeriods}
                                            validateRules={{
                                                required: (value) => {
                                                    const range = getValues('automationRange');
                                                    const automationPeriods = value as string;
                                                    if (
                                                        range != null &&
                                                        range.id === AutomationRange.SPECIFIC_RANGE &&
                                                        automationPeriods == null
                                                    )
                                                        return t('validation.required');
                                                    return true;
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                        {/* COLUMN 3 */}
                        <div className={cn(styles.borderRight, 'k-d-flex-col k-gap-3 k-overflow-y-auto k-px-4 k-py-3')}>
                            <Card
                                data-testid="tri-proportions-ot-thresholds"
                                className="k-min-h-min "
                                title={t('thresholds')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-row ">
                                    <BasicDropDownController
                                        label={t('labels.thresholdType')}
                                        control={control}
                                        name="thresholdType"
                                        data-testid="threshold-type"
                                        data={ThresholdTypeOptions || []}
                                        dataItemKey="id"
                                        textField="name"
                                        required
                                        allowClear
                                        readOnly={!isEditing}
                                        filterable
                                        onChange={async (event) => {
                                            const value = event.value as { id: string; name: string };
                                            if (value) {
                                                if (value.id !== ProportionsOTThresholdType.FIXED) {
                                                    setValue('upperRed', 3);
                                                    setValue('lowerRed', 3);
                                                    setValue('upperAmber', 2);
                                                    setValue('lowerAmber', 2);
                                                    setValue('studyType', null);
                                                    setValue('countryValue', null);
                                                    setUpdatedBenchMarkOptions(
                                                        BenchmarkTypeOptions.filter(
                                                            (item) => item.id !== ProportionsOTBenchmarkType.NONE,
                                                        ),
                                                    );
                                                } else {
                                                    setValue('upperRed', null);
                                                    setValue('lowerRed', null);
                                                    setValue('upperAmber', null);
                                                    setValue('lowerAmber', null);
                                                    setValue('studyType', null);
                                                    setValue('countryValue', null);
                                                    setUpdatedBenchMarkOptions(BenchmarkTypeOptions);
                                                }
                                            }
                                            await trigger(['upperRed', 'lowerRed', 'upperAmber', 'lowerAmber']);
                                        }}
                                    />
                                </div>
                                <div className="k-d-flex-row k-gap-3 k-mt-2">
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.upperRed')}
                                            control={control}
                                            name="upperRed"
                                            data-testid="upper-red"
                                            readOnly={!isEditing}
                                            disabled={disableThresholds}
                                            min={0}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const upperAmber = getValues('upperAmber');
                                                    const thresholdType = getValues('thresholdType');
                                                    if (
                                                        upperAmber != null &&
                                                        value != null &&
                                                        (value as number) <= upperAmber
                                                    )
                                                        return t('validation.upperRedThresholdOrder');

                                                    if (
                                                        thresholdType?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        thresholdType?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value == null &&
                                                        upperAmber != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                                scaleByComparison: (value: number | null) => {
                                                    if (!value) return true;

                                                    const thresholdType = getValues('thresholdType')?.id;
                                                    if (thresholdType !== ProportionsOTThresholdType.FIXED) return true;

                                                    const scaleBy = getValues('scaleBy')?.id;

                                                    const scaleByValue = getScaleByNumber(scaleBy);

                                                    if (scaleByValue !== undefined && value > scaleByValue)
                                                        return t('validation.lowerFixedThresholdScaleBy');

                                                    return true;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger(['upperAmber']);
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        {' '}
                                        <NumericTextBoxController
                                            label={t('labels.upperAmber')}
                                            control={control}
                                            name="upperAmber"
                                            data-testid="upper-amber"
                                            readOnly={!isEditing}
                                            disabled={disableThresholds}
                                            min={0}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const upperRed = getValues('upperRed');
                                                    const thresholdType = getValues('thresholdType');
                                                    if (
                                                        upperRed != null &&
                                                        value != null &&
                                                        (value as number) >= upperRed
                                                    )
                                                        return t('validation.upperAmberThresholdOrder');

                                                    if (
                                                        thresholdType?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        thresholdType?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value == null &&
                                                        upperRed != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                                scaleByComparison: (value: number | null) => {
                                                    if (!value) return true;

                                                    const thresholdType = getValues('thresholdType')?.id;
                                                    if (thresholdType !== ProportionsOTThresholdType.FIXED) return true;

                                                    const scaleBy = getValues('scaleBy')?.id;

                                                    const scaleByValue = getScaleByNumber(scaleBy);

                                                    if (scaleByValue !== undefined && value > scaleByValue)
                                                        return t('validation.lowerFixedThresholdScaleBy');

                                                    return true;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger('upperRed');
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="k-d-flex-row k-gap-3 k-mt-2">
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.lowerAmber')}
                                            control={control}
                                            name="lowerAmber"
                                            data-testid="lower-amber"
                                            readOnly={!isEditing}
                                            min={0}
                                            disabled={disableThresholds}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const lowerRed = getValues('lowerRed');
                                                    const threshold = getValues('thresholdType') as {
                                                        id: string;
                                                        name: string;
                                                    };
                                                    if (
                                                        threshold.id === ProportionsOTThresholdType.FIXED &&
                                                        lowerRed != null &&
                                                        value != null &&
                                                        (value as number) <= lowerRed
                                                    ) {
                                                        return t('validation.lowerAmberThresholdFixedOrder');
                                                    }
                                                    if (
                                                        threshold.id !== ProportionsOTThresholdType.FIXED &&
                                                        lowerRed != null &&
                                                        value != null &&
                                                        (value as number) >= lowerRed
                                                    )
                                                        return t('validation.lowerAmberThresholdNotFixedOrder');

                                                    if (
                                                        threshold?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        threshold?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value == null &&
                                                        lowerRed != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                                scaleByComparison: (value: number | null) => {
                                                    if (!value) return true;

                                                    const thresholdType = getValues('thresholdType')?.id;
                                                    if (thresholdType !== ProportionsOTThresholdType.FIXED) return true;

                                                    const scaleBy = getValues('scaleBy')?.id;

                                                    const scaleByValue = getScaleByNumber(scaleBy);

                                                    if (scaleByValue !== undefined && value > scaleByValue)
                                                        return t('validation.lowerFixedThresholdScaleBy');

                                                    return true;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger(['lowerRed', 'thresholdType']);
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        <NumericTextBoxController
                                            label={t('labels.lowerRed')}
                                            control={control}
                                            name="lowerRed"
                                            data-testid="lower-red"
                                            readOnly={!isEditing}
                                            disabled={disableThresholds}
                                            min={0}
                                            validateRules={{
                                                valueOrder: (value: number | null) => {
                                                    const lowerAmber = getValues('lowerAmber');
                                                    const threshold = getValues('thresholdType') as {
                                                        id: string;
                                                        name: string;
                                                    };
                                                    if (
                                                        threshold.id === ProportionsOTThresholdType.FIXED &&
                                                        lowerAmber != null &&
                                                        value != null &&
                                                        (value as number) >= lowerAmber
                                                    ) {
                                                        return t('validation.lowerRedThresholdFixedOrder');
                                                    }
                                                    if (
                                                        threshold.id !== ProportionsOTThresholdType.FIXED &&
                                                        lowerAmber != null &&
                                                        value != null &&
                                                        (value as number) <= lowerAmber
                                                    )
                                                        return t('validation.lowerRedThresholdNotFixedOrder');

                                                    if (
                                                        threshold?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value != null &&
                                                        (value as number) <= 0
                                                    ) {
                                                        return t('validation.valueGreaterThan0');
                                                    }

                                                    if (
                                                        threshold?.id !== ProportionsOTThresholdType.FIXED &&
                                                        value == null &&
                                                        lowerAmber != null
                                                    )
                                                        return t('validation.required');

                                                    return undefined;
                                                },
                                                scaleByComparison: (value: number | null) => {
                                                    if (!value) return true;

                                                    const thresholdType = getValues('thresholdType')?.id;
                                                    if (thresholdType !== ProportionsOTThresholdType.FIXED) return true;

                                                    const scaleBy = getValues('scaleBy')?.id;

                                                    const scaleByValue = getScaleByNumber(scaleBy);

                                                    if (scaleByValue !== undefined && value > scaleByValue)
                                                        return t('validation.lowerFixedThresholdScaleBy');

                                                    return true;
                                                },
                                            }}
                                            handleInputChange={async () => {
                                                await trigger(['lowerAmber', 'thresholdType']);
                                            }}
                                        />
                                    </div>
                                </div>
                            </Card>
                            <Card
                                data-testid="tri-proportions-ot-thresholds"
                                className="k-min-h-min"
                                title={t('benchmarks')}
                                bodyClassName="!k-pt-2"
                            >
                                <div className="k-d-flex-row k-gap-3">
                                    <div className="k-flex-1">
                                        <BasicDropDownController
                                            label={t('labels.studyType')}
                                            control={control}
                                            name="studyType"
                                            data-testid="study-type"
                                            data={updatedBenchMarkOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                            disabled={disableThresholds}
                                            onChange={async (event) => {
                                                const value = event.value as { id: string; name: string };
                                                const thresholdType = getValues('thresholdType');
                                                if (value && thresholdType) {
                                                    if (
                                                        thresholdType.id === ProportionsOTThresholdType.FIXED &&
                                                        value.id !== ProportionsOTBenchmarkType.STUDY_SPECIFIC
                                                    ) {
                                                        setUpdatedCountryTypeOptions(
                                                            CountryBenchmarkTypeOptions.filter(
                                                                (item) =>
                                                                    item.id !==
                                                                    ProportionsOTCountryBenchmarkType.STUDY_SPECIFIC,
                                                            ),
                                                        );
                                                    } else if (
                                                        thresholdType.id !== ProportionsOTThresholdType.FIXED &&
                                                        value.id === ProportionsOTBenchmarkType.STUDY_SPECIFIC
                                                    ) {
                                                        setUpdatedCountryTypeOptions(
                                                            CountryBenchmarkTypeOptions.filter(
                                                                (item) =>
                                                                    item.id !== ProportionsOTCountryBenchmarkType.NONE,
                                                            ),
                                                        );
                                                    } else if (
                                                        thresholdType.id !== ProportionsOTThresholdType.FIXED &&
                                                        value.id !== ProportionsOTBenchmarkType.STUDY_SPECIFIC
                                                    ) {
                                                        setUpdatedCountryTypeOptions(
                                                            CountryBenchmarkTypeOptions.filter(
                                                                (item) =>
                                                                    item.id !==
                                                                        ProportionsOTCountryBenchmarkType.NONE &&
                                                                    item.id !==
                                                                        ProportionsOTCountryBenchmarkType.STUDY_SPECIFIC,
                                                            ),
                                                        );
                                                    } else {
                                                        setUpdatedCountryTypeOptions(CountryBenchmarkTypeOptions);
                                                    }
                                                }
                                                setValue('countryValue', null);
                                                setValue('studySpecificValue', null);
                                            }}
                                        />
                                    </div>
                                    <div className="k-flex-1">
                                        {showStudySpecific && (
                                            <NumericTextBoxController
                                                label={t('labels.studySpecificValue')}
                                                control={control}
                                                name="studySpecificValue"
                                                data-testid="study-specific-value"
                                                readOnly={!isEditing}
                                                required
                                                validateRules={{
                                                    scaleByComparison: (value: number | null) => {
                                                        if (!value) return true;

                                                        const scaleBy = getValues('scaleBy')?.id;

                                                        const scaleByValue = getScaleByNumber(scaleBy);

                                                        if (scaleByValue !== undefined && value > scaleByValue)
                                                            return t('validation.lowerStudySpecificScaleBy');

                                                        return true;
                                                    },
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="k-d-flex-row k-gap-3">
                                    <div className="k-flex-1 k-mt-2">
                                        <BasicDropDownController
                                            label={t('labels.countryValue')}
                                            control={control}
                                            name="countryValue"
                                            data-testid="country-value"
                                            data={updatedCountryTypeOptions || []}
                                            dataItemKey="id"
                                            textField="name"
                                            required
                                            allowClear
                                            readOnly={!isEditing}
                                            filterable
                                            disabled={disableCountryType}
                                        />
                                    </div>
                                </div>
                            </Card>
                        </div>
                        {/* COLUMN 4 */}
                        <div
                            className={cn(styles.borderRight, 'k-d-flex-col k-gap-3 k-overflow-y-auto k-pt-3 k-pb-1.5')}
                        >
                            <div className="k-px-4">
                                <Card
                                    data-testid="tri-proportions-ot-eligibility"
                                    className="k-min-h-min"
                                    title={t('eligibility')}
                                    bodyClassName="!k-pt-2"
                                >
                                    <div className="k-d-flex-row k-gap-3">
                                        <div className="k-flex-1">
                                            <NumericTextBoxController
                                                label={t('labels.eligibilityUnit')}
                                                control={control}
                                                name="eligibilityUnit"
                                                data-testid="eligibility-unit"
                                                readOnly={!isEditing}
                                                max={30}
                                                min={1}
                                                validateRules={{
                                                    required: (value) => {
                                                        const unit = value as string;
                                                        const period = getValues('eligibilityPeriod');
                                                        if (period != null && unit == null)
                                                            return t('validation.required');
                                                    },
                                                }}
                                                handleInputChange={async () => {
                                                    await trigger(['eligibilityPeriod']);
                                                }}
                                            />
                                        </div>
                                        <div className="k-flex-1">
                                            <BasicDropDownController
                                                label={t('labels.eligibilityPeriod')}
                                                control={control}
                                                name="eligibilityPeriod"
                                                data-testid="eligibility-period"
                                                data={EligibilityPeriodOptions || []}
                                                dataItemKey="id"
                                                textField="name"
                                                allowClear
                                                readOnly={!isEditing}
                                                filterable
                                                onChange={async () => {
                                                    await trigger(['eligibilityUnit']);
                                                }}
                                                validateRules={{
                                                    required: (value) => {
                                                        const period = value?.id as string;
                                                        const unit = getValues('eligibilityUnit');
                                                        if (period == null && unit != null)
                                                            return t('validation.required');
                                                        return true;
                                                    },
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="k-d-flex-row k-gap-3 k-px-4">
                                <div className="k-flex-1">
                                    <NumericTextBoxController
                                        label={t('labels.expectedVolume')}
                                        control={control}
                                        name="expectedVolume"
                                        data-testid="expected-volume"
                                        readOnly={!isEditing}
                                        min={1}
                                        validationMessage={t('validation.valueGreaterThan0')}
                                    />
                                </div>
                                <div className="k-flex-1">
                                    <NumericTextBoxController
                                        label={t('labels.xAxisPtsToShade')}
                                        control={control}
                                        name="xAxisPtsToShade"
                                        data-testid="x-axis-pts-to-shade"
                                        readOnly={!isEditing}
                                        min={1}
                                        validationMessage={t('validation.valueGreaterThan0')}
                                    />
                                </div>
                            </div>

                            <div className="k-d-flex-row k-gap-3 k-px-4">
                                <TextBoxController
                                    label={t('labels.numeratorName')}
                                    data-testid="numerator-name"
                                    name="numeratorName"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <div className="k-d-flex-row k-gap-3 k-mt--2 k-px-4">
                                <TextBoxController
                                    label={t('labels.denominatorName')}
                                    data-testid="denominator-name"
                                    name="denominatorName"
                                    control={control}
                                    required
                                    maxLength={50}
                                    readOnly={!isEditing}
                                />
                            </div>
                            <dl className={cn(styles.auditInfo)}>
                                <dt>{t('labels.created')}:</dt>
                                <dd>
                                    {panel && (
                                        <>
                                            {stringDateWithoutTimeFromUtcNonNullable(panel.created, 'DD-MMM-YYYY')} by{' '}
                                            {panel.createdBy?.firstName} {panel.createdBy?.lastName}
                                        </>
                                    )}
                                </dd>

                                <dt>{t('labels.updated')}:</dt>
                                <dd>
                                    {panel && (
                                        <>
                                            {stringDateWithoutTimeFromUtcNonNullable(panel.updated, 'DD-MMM-YYYY')} by{' '}
                                            {panel.updatedBy?.firstName} {panel.updatedBy?.lastName}
                                        </>
                                    )}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            )}
            {chip === ChipValues.Preview && (
                <PanelPreview
                    processedConfig={processedConfig}
                    currentConfig={currentConfig as unknown as panel_OpraQueries_panel_Panel}
                    isValidConfig={isValid}
                />
            )}
        </div>
    );
    // Show a regular card if we want to hide the edit button (on the ALL panels tab)
    const CardToUse = source === 'All' ? Card : EditCard;

    return (
        <>
            {showReasonForChangeDialog && (
                <PanelReasonForChangeDialog
                    onCancel={() => setShowReasonForChangeDialog(false)}
                    onSubmit={async (reasonForChange) => {
                        setShowReasonForChangeDialog(false);
                        await submit(reasonForChange);
                    }}
                />
            )}
            <CardToUse
                className="k-h-full"
                title={panel ? `${t('proportionsOT')}: ${panel.recordId}` : t('proportionsOT')}
                onEdit={() => {
                    setIsEditing(true);
                }}
                padding="none"
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges();
                        if (!confirmed) return;
                    }
                    setChip(ChipValues.Configuration);
                    setIsEditing(false);
                    reset(defaultFormValues);
                    onCancelOverride?.();
                }}
                onSave={async () => {
                    if (panel && isValid) {
                        setShowReasonForChangeDialog(true);
                    } else {
                        await submit();
                    }
                }}
                isEditMode={isEditing}
                data-testid="test-id"
                saveDisabled={isSaving || !isDirty || !isValid}
                disabled={!canEdit}
            >
                {isSaving || isLoading ? <Loading /> : content}
            </CardToUse>
        </>
    );
};

const Wrapper = ({ panelRef, createData, canEdit }: IProps) => {
    const {
        loading: isLoading,
        refetch,
        data,
    } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef },
        skip: !panelRef,
        fetchPolicy: 'no-cache',
    });

    const { state } = useLocalState();

    // Getting the processed config to display the preview for previously processed configuration.

    const { data: latestDataset, isLoading: datasetLoading } = useGetLatestDataSet(state?.selectedStudy?.id);
    const latestDatasetDate = latestDataset?.created;

    const { data: processedConfig, loading: isConfigLoading } = useQuery<IPanel, IPanelVariables>(GET_PANEL, {
        variables: { panelId: panelRef, timestamp: latestDatasetDate },
        skip: !panelRef || !latestDatasetDate,
        fetchPolicy: 'no-cache',
    });

    if (isLoading || isConfigLoading || datasetLoading) {
        return <Loading />;
    }

    return (
        <ProportionsOT
            key={data?.panel?.auditId}
            panel={data?.panel ?? undefined}
            onCancelOverride={createData?.onCancel}
            onCreate={async (newPanelRef: string) => {
                await createData?.onCreate(newPanelRef);
            }}
            onUpdate={async () => {
                await refetch();
            }}
            canEdit={canEdit}
            processedConfig={processedConfig?.panel ?? undefined}
        />
    );
};

export default Wrapper;
